import { forwardRef } from 'react';
import styled from '@emotion/styled';
import type { StackProps, StackAlign, StackJustify, SpacingValue } from './Stack.types';

/**
 * Spacing map based on production theme
 */
const spacingMap: Record<number, string> = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  9: '36px',
  10: '40px',
};

/**
 * Alignment map for flex items
 */
const alignMap: Record<StackAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};

/**
 * Justify map for flex content
 */
const justifyMap: Record<StackJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
};

/**
 * Helper to resolve spacing value
 */
const resolveSpacing = (value: SpacingValue | undefined): string => {
  if (value === undefined) return '0';
  if (typeof value === 'number') {
    return spacingMap[value] || `${value * 4}px`;
  }
  return value;
};

/**
 * Styled Stack container
 */
const StyledStack = styled.div<{
  $direction: 'column' | 'row';
  $gap: string;
  $align: string;
  $justify: string;
  $wrap: boolean;
}>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  gap: ${({ $gap }) => $gap};
  align-items: ${({ $align }) => $align};
  justify-content: ${({ $justify }) => $justify};
  flex-wrap: ${({ $wrap, $direction }) => ($direction === 'row' && $wrap ? 'wrap' : 'nowrap')};
`;

/**
 * Stack component - A flex container with gap that supports both vertical and horizontal layouts
 *
 * @example
 * ```tsx
 * // Vertical stack (default)
 * <Stack gap={4}>
 *   <Child1 />
 *   <Child2 />
 * </Stack>
 *
 * // Horizontal stack
 * <Stack direction="horizontal" gap={2}>
 *   <Button />
 *   <Button />
 * </Stack>
 *
 * // Horizontal with wrapping
 * <Stack direction="horizontal" gap={2} wrap>
 *   {tags.map(tag => <Tag key={tag.id}>{tag.name}</Tag>)}
 * </Stack>
 *
 * // Centered content
 * <Stack gap={2} align="center" justify="center">
 *   <Title />
 *   <Description />
 * </Stack>
 *
 * // As a section element
 * <Stack as="section" gap={3}>
 *   <Header />
 *   <Content />
 * </Stack>
 * ```
 */
export const Stack = forwardRef<HTMLElement, StackProps>(
  (
    {
      children,
      direction = 'vertical',
      gap = 0,
      align,
      justify = 'start',
      wrap = false,
      as: Component = 'div',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    // Default align based on direction for backward compatibility
    // vertical (column): stretch, horizontal (row): center
    const defaultAlign = direction === 'horizontal' ? 'center' : 'stretch';
    const resolvedAlign = alignMap[align ?? defaultAlign];

    const resolvedGap = resolveSpacing(gap);
    const resolvedJustify = justifyMap[justify];
    const flexDirection = direction === 'horizontal' ? 'row' : 'column';

    return (
      <StyledStack
        as={Component}
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        $direction={flexDirection}
        $gap={resolvedGap}
        $align={resolvedAlign}
        $justify={resolvedJustify}
        $wrap={wrap}
        data-testid={dataTestId || testId}
        data-id={dataId}
        {...rest}
      >
        {children}
      </StyledStack>
    );
  }
);

Stack.displayName = 'Stack';

export default Stack;
